import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import toast from 'react-hot-toast';
import { formatDateSafe } from '../../lib/utils';

const { FiEdit, FiTrash2, FiPlus, FiArrowLeft, FiSearch } = FiIcons;

const ManageContent = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    const { data } = await supabase
      .from('entries_20240520')
      .select('*')
      .order('date', { ascending: false });
    setEntries(data || []);
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this memory?')) return;
    const { error } = await supabase.from('entries_20240520').delete().eq('id', id);
    if (!error) {
      toast.success('Deleted');
      setEntries(entries.filter(e => e.id !== id));
    }
  };

  const filtered = entries.filter(e => 
    e.title.toLowerCase().includes(search.toLowerCase()) ||
    e.section.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="max-w-5xl mx-auto px-4 py-12">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12 gap-6">
        <div>
          <Link to="/admin" className="text-sm font-bold text-[#8c7851] flex items-center mb-2 hover:underline">
            <SafeIcon icon={FiArrowLeft} className="mr-2" />
            Dashboard
          </Link>
          <h1 className="text-4xl font-serif font-bold text-[#2c2c2c]">Manage Archive</h1>
          <p className="text-stone-500 mt-1">Review and organize your family stories.</p>
        </div>
        <Link to="/admin/entries/new" className="bg-[#2c2c2c] text-white px-8 py-4 rounded-2xl font-bold flex items-center gap-2 hover:bg-black transition-all shadow-lg active:scale-95">
          <SafeIcon icon={FiPlus} />
          <span>New Memory</span>
        </Link>
      </div>

      <div className="bg-white rounded-[2.5rem] border border-stone-200 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-stone-100 bg-stone-50/50">
          <div className="relative max-w-md">
            <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
            <input 
              placeholder="Search by title or section..." 
              value={search} 
              onChange={e => setSearch(e.target.value)} 
              className="w-full pl-12 pr-6 py-3 rounded-xl border border-stone-200 outline-none focus:ring-4 focus:ring-[#8c7851]/5 focus:border-[#8c7851] transition-all" 
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-stone-50/80 text-[10px] uppercase tracking-[0.2em] font-bold text-stone-400 border-b border-stone-100">
              <tr>
                <th className="px-8 py-5">Memory Details</th>
                <th className="px-8 py-5">Section</th>
                <th className="px-8 py-5">Date</th>
                <th className="px-8 py-5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-50">
              {loading ? (
                [1,2,3].map(i => (
                  <tr key={i} className="animate-pulse">
                    <td colSpan="4" className="px-8 py-6 h-20 bg-stone-50/30" />
                  </tr>
                ))
              ) : filtered.length > 0 ? (
                filtered.map(entry => (
                  <tr key={entry.id} className="hover:bg-stone-50/50 transition-colors group">
                    <td className="px-8 py-6">
                      <span className="font-bold text-[#2c2c2c] block group-hover:text-[#8c7851] transition-colors">{entry.title}</span>
                      <span className="text-xs text-stone-400 mt-0.5 block">{entry.folder_name || 'General'}</span>
                    </td>
                    <td className="px-8 py-6">
                      <span className="text-[10px] uppercase font-bold px-3 py-1 bg-white border border-stone-100 rounded-full text-stone-500 shadow-sm">
                        {entry.section}
                      </span>
                    </td>
                    <td className="px-8 py-6 text-sm font-medium text-stone-600">
                      {formatDateSafe(entry.date, 'MMM d, yyyy')}
                    </td>
                    <td className="px-8 py-6 text-right">
                      <div className="flex justify-end gap-2">
                        <Link to={`/admin/entries/edit/${entry.id}`} className="p-2.5 text-stone-400 hover:text-[#8c7851] hover:bg-white rounded-xl transition-all border border-transparent hover:border-stone-100 shadow-sm">
                          <SafeIcon icon={FiEdit} className="w-4 h-4" />
                        </Link>
                        <button onClick={() => handleDelete(entry.id)} className="p-2.5 text-stone-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all border border-transparent hover:border-red-100">
                          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="px-8 py-20 text-center italic text-stone-400">
                    No memories found matching your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ManageContent;